/// ATC voice readback via the browser's Web Speech API.
///
/// One utterance at a time. New transmissions cancel anything mid-
/// flight so a player issuing rapid commands doesn't fall behind a
/// stale 4-second-old "FOA101, climb and maintain 5,000" while
/// they've already issued three more. Mirrors iOS's
/// `VoiceManager.maxPending = 1` behaviour.
///
/// Browser autoplay policies: `speechSynthesis.speak()` is allowed
/// without a user gesture in most browsers, but Safari can be
/// finicky. We don't gate explicitly — if the first utterance is
/// dropped silently by Safari, the second one (issued after a
/// click anywhere on the canvas) lands. That's acceptable for an
/// app where you can't actually do anything without clicking.

const VOICE_PREF_KEY = 'flatout.web.voice'
type VoicePref = 'on' | 'off'

function getVoicePref(): VoicePref {
  try {
    const v = window.localStorage.getItem(VOICE_PREF_KEY)
    return v === 'off' ? 'off' : 'on'
  } catch {
    return 'on'
  }
}

export function setVoicePref(pref: VoicePref): void {
  try {
    window.localStorage.setItem(VOICE_PREF_KEY, pref)
  } catch {
    // private mode — best-effort only
  }
  if (pref === 'off') cancelAll()
}

export function isVoiceEnabled(): boolean {
  return getVoicePref() === 'on'
}

function isAvailable(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window
}

/// Speak the given line via the browser's TTS. No-op if voice is
/// off, the API isn't available, or the line is empty.
///
/// Sanitises the input first — emoji like "⚠️" speech-synthesises
/// as literal "warning sign" in some voices, which sounds wrong
/// in radio chatter. Strip them before queuing.
export function speak(text: string): void {
  if (!isVoiceEnabled()) return
  if (!isAvailable()) return
  const cleaned = sanitise(text)
  if (!cleaned) return
  // Cancel anything in progress / queued. Only the latest line
  // matters in radio chatter — a stale ack catching up after the
  // player has already moved on is worse than silence.
  window.speechSynthesis.cancel()
  const u = new SpeechSynthesisUtterance(cleaned)
  // Conversational pace. Real-world ATC chatter IS faster than this,
  // but TTS voices read fast text mushy — clipped consonants, lost
  // numbers. 1.0 keeps the readback intelligible at the cost of
  // some authenticity.
  u.rate = 1.0
  u.pitch = 1.0
  u.volume = 1.0
  // Pin the voice to the system default English voice when one
  // exists. Mac Safari surfaces the Siri voices; Chrome surfaces
  // the Google voices; both are clean. Avoid eSpeak / Microsoft
  // Anna where possible — they sound like a 1998 GPS.
  const v = pickVoice()
  if (v) u.voice = v
  window.speechSynthesis.speak(u)
}

/// Cancel any in-progress / queued speech. Called when the player
/// disables voice mid-session, when the level ends, etc.
export function cancelAll(): void {
  if (!isAvailable()) return
  window.speechSynthesis.cancel()
}

function sanitise(text: string): string {
  // Strip emoji / pictographs / surrogate-pair junk; keep digits,
  // letters, common punctuation. The regex covers the unicode
  // ranges the SpeechSynthesisUtterance pronounces literally.
  return text
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
}

let cachedVoice: SpeechSynthesisVoice | null | undefined = undefined

function pickVoice(): SpeechSynthesisVoice | null {
  if (!isAvailable()) return null
  if (cachedVoice !== undefined) return cachedVoice
  const voices = window.speechSynthesis.getVoices()
  if (voices.length === 0) {
    // Voice list lazy-loads on some browsers — leave undefined so
    // the next call retries. The current utterance falls back to
    // the browser default, which is fine.
    return null
  }
  const en = voices.filter((v) => v.lang.toLowerCase().startsWith('en'))
  // Prefer voices that contain "Google", "Siri", "Microsoft" — in
  // that order — over the eSpeak fallback on Linux.
  const preferred =
    en.find((v) => /google/i.test(v.name)) ??
    en.find((v) => /siri/i.test(v.name)) ??
    en.find((v) => /microsoft/i.test(v.name)) ??
    en[0] ??
    voices[0]
  cachedVoice = preferred ?? null
  return cachedVoice
}

// Voice list arrives async on Chrome — invalidate the cache so
// the second call picks the real preferred voice instead of the
// arbitrary first-load default.
if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  window.speechSynthesis.addEventListener?.('voiceschanged', () => {
    cachedVoice = undefined
  })
}
